import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  WarningAmber as WarningAmberIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { DASHBOARD_COLORS, sectionPaperSx } from './dashboardStyles';

const AlertRow = ({ icon: Icon, message, severity, actionLabel, onAction, showDivider }) => {
  const color =
    severity === 'urgent'
      ? DASHBOARD_COLORS.danger
      : severity === 'warning'
        ? DASHBOARD_COLORS.warning
        : DASHBOARD_COLORS.info;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          py: 1.5,
        }}
      >
        <Box display="flex" alignItems="flex-start" gap={1.25} flex={1} minWidth={0}>
          <Icon sx={{ color, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color, fontWeight: 500, lineHeight: 1.45 }}>
            {message}
          </Typography>
        </Box>
        {actionLabel && onAction && (
          <Button
            size="small"
            variant="outlined"
            onClick={onAction}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.75rem',
              borderColor: `${color}55`,
              color,
              bgcolor: `${color}0a`,
              minWidth: 72,
              '&:hover': { borderColor: color, bgcolor: `${color}14` },
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Box>
      {showDivider && <Divider />}
    </>
  );
};

const AlertsPanel = ({ alerts, loading }) => (
  <Paper
    elevation={0}
    sx={{
      ...sectionPaperSx,
      height: '100%',
      minHeight: 320,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      <WarningAmberIcon sx={{ color: DASHBOARD_COLORS.danger, fontSize: 22 }} />
      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: DASHBOARD_COLORS.text }}>
        Alerts &amp; Notifications
      </Typography>
    </Box>

    {loading ? (
      <Box flex={1} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress size={28} />
      </Box>
    ) : alerts.length === 0 ? (
      <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1} py={4}>
        <CheckCircleOutlineIcon sx={{ fontSize: 40, color: DASHBOARD_COLORS.sales }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No urgent alerts right now. You&apos;re all caught up.
        </Typography>
      </Box>
    ) : (
      <Stack spacing={0} sx={{ flex: 1 }}>
        {alerts.map((alert, index) => (
          <AlertRow key={alert.id} {...alert} showDivider={index < alerts.length - 1} />
        ))}
      </Stack>
    )}
  </Paper>
);

export default AlertsPanel;
